import React from "react";
import { AlertTriangle } from "lucide-react";

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
    CaptionViolationMetrics,
    REASON_DESCRIPTIONS,
    REASON_LABELS,
} from "./types";

// The rate above which this stops being noise and becomes worth investigating (model
// drift, or a listing pattern the caption rules do not handle). It sat around 2% of
// slides before the caption rules landed; near-zero is normal now. Used to colour the
// number, so the reader is not left to judge a bare percentage.
const ALERT_RATE = 0.02;

interface CaptionViolationMetricCardsProps {
    metrics: CaptionViolationMetrics;
    className?: string;
}

const CaptionViolationMetricCards: React.FC<CaptionViolationMetricCardsProps> = ({
    metrics,
    className,
}) => {
    const isMobile = useIsMobile(900);
    const pct = (n: number): string => `${(n * 100).toFixed(2)}%`;
    const overAlertRate = metrics.slideViolationRate > ALERT_RATE;

    // Headroom on the y-axis. Without it the tallest bar is always exactly 100% of the
    // plot — so a window with a single day (the common case early on) renders as a solid
    // slab filling the whole card, which reads as a broken element rather than a chart.
    const maxDay =
        Math.max(
            ...metrics.trend.map((d) => d.slideViolationRate),
            ALERT_RATE,
            0.0001
        ) * 1.3;

    return (
        <div className={cn("flex flex-col gap-4", className)}>
            <div
                className={cn(
                    "grid gap-4",
                    isMobile ? "grid-cols-2" : "grid-cols-4"
                )}
            >
                {/* The headline. A raw count cannot tell "we got worse" from "we got
                    busier" — every render writes a row, so this is a true rate. */}
                <Card className="mt-0 flex flex-col">
                    <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 p-4 pb-1">
                        <CardTitle className="text-left text-sm font-semibold">
                            Slides that failed validation
                        </CardTitle>
                        {overAlertRate && (
                            <AlertTriangle className="h-4 w-4 shrink-0 text-[#ff6600]" />
                        )}
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        <div
                            className={cn(
                                "text-2xl font-bold",
                                overAlertRate ? "text-[#d50000]" : "text-primary"
                            )}
                        >
                            {pct(metrics.slideViolationRate)}
                        </div>
                        <div className="text-xs text-gray-500">
                            {overAlertRate
                                ? "Above 2% — worth investigating"
                                : "Normal below 2%"}
                        </div>
                    </CardContent>
                </Card>

                <Card className="mt-0 flex flex-col">
                    <CardHeader className="p-4 pb-1">
                        <CardTitle className="text-left text-sm font-semibold">
                            Violating slides
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        <div className="text-2xl font-bold text-primary">
                            {metrics.violatingSlides.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                            of {metrics.slides.toLocaleString()} slides captioned
                        </div>
                    </CardContent>
                </Card>

                <Card className="mt-0 flex flex-col">
                    <CardHeader className="p-4 pb-1">
                        <CardTitle className="text-left text-sm font-semibold">
                            Affected renders
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        <div className="text-2xl font-bold text-primary">
                            {metrics.rendersWithViolations.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                            of {metrics.renders.toLocaleString()} renders
                        </div>
                    </CardContent>
                </Card>

                <Card className="mt-0 flex flex-col">
                    <CardHeader className="p-4 pb-1">
                        <CardTitle className="text-left text-sm font-semibold">
                            Window
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        <div className="text-2xl font-bold text-primary">
                            {metrics.windowDays}d
                        </div>
                        <div className="text-xs text-gray-500">
                            debug records expire after 90 days
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                {/* WHICH rule is surviving the retry budget — this is what turns a
                    rising rate into somewhere to actually look. */}
                <Card className="mt-0">
                    <CardHeader className="p-4 pb-2">
                        <CardTitle className="text-left text-sm font-semibold">
                            Which rule is getting through
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        {metrics.byReason.length === 0 ? (
                            // "Nothing got through" and "nothing was measured" are
                            // different answers; saying "no violations" when no video
                            // rendered implies a clean bill of health we never took.
                            <div className="text-sm text-gray-500">
                                {metrics.renders === 0
                                    ? "No videos rendered in this window."
                                    : `No rules broken across ${metrics.renders.toLocaleString()} render${metrics.renders === 1 ? "" : "s"}.`}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {metrics.byReason.map(({ reason, slides }) => {
                                    const share =
                                        metrics.violatingSlides > 0
                                            ? slides / metrics.violatingSlides
                                            : 0;
                                    return (
                                        <div
                                            key={reason}
                                            className="flex flex-col gap-1"
                                            title={REASON_DESCRIPTIONS[reason]}
                                        >
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-primary">
                                                    {REASON_LABELS[reason]}
                                                </span>
                                                <span className="text-gray-500">
                                                    {slides}
                                                </span>
                                            </div>
                                            <div className="h-2 w-full rounded bg-gray-100">
                                                <div
                                                    className="h-2 rounded bg-secondary"
                                                    style={{
                                                        width: `${Math.max(share * 100, 2)}%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Trend. Deliberately plain bars: neither repo has a charting library
                    wired in, and adding one for a sparkline is not a trade worth making. */}
                <Card className="mt-0">
                    <CardHeader className="p-4 pb-2">
                        <CardTitle className="text-left text-sm font-semibold">
                            Violation rate per day
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        {/* Three states, not two. "No renders" and "renders, but zero
                            violations" are completely different answers, and zero
                            violations is the EXPECTED state — so it must not render as a
                            row of 0-height bars indistinguishable from having no data. A
                            monitoring card that looks the same when it is healthy and
                            when it is blind is worse than no card. */}
                        {metrics.renders === 0 ? (
                            <div className="flex h-24 items-center justify-center text-sm text-gray-500">
                                No videos rendered in this window.
                            </div>
                        ) : metrics.violatingSlides === 0 ? (
                            <div className="flex h-24 flex-col items-center justify-center gap-1">
                                <div className="text-2xl font-bold text-primary">
                                    0%
                                </div>
                                <div className="text-xs text-gray-500">
                                    No violations across{" "}
                                    {metrics.renders.toLocaleString()} render
                                    {metrics.renders === 1 ? "" : "s"} (
                                    {metrics.slides.toLocaleString()} slides)
                                </div>
                            </div>
                        ) : (
                            /* Fixed-width bars, so one day is a bar not a full-card slab —
                               but a 30/90-day window is many bars, so scroll horizontally
                               (w-max content inside overflow-x-auto) rather than letting
                               them spill past the card. Bars and labels share one scroll
                               container so they stay aligned. Same pattern as the table. */
                            <div className="overflow-x-auto">
                                <div className="flex w-max flex-col gap-1">
                                    <div className="relative flex h-24 items-end gap-2">
                                        {/* The 2% line the rate is judged against. A bar
                                            means nothing without the threshold beside it. */}
                                        <div
                                            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-300"
                                            style={{
                                                bottom: `${(ALERT_RATE / maxDay) * 100}%`,
                                            }}
                                        />
                                        {metrics.trend.map((d) => (
                                            <div
                                                key={d.day}
                                                className="flex h-full w-10 flex-col items-center justify-end"
                                                title={`${d.day} — ${pct(d.slideViolationRate)} (${d.violatingSlides}/${d.slides} slides, ${d.renders} render${d.renders === 1 ? "" : "s"})`}
                                            >
                                                <div
                                                    className={cn(
                                                        "w-full rounded-t",
                                                        // A clean day still gets a visible
                                                        // floor, so it reads as "measured
                                                        // and fine" rather than "missing".
                                                        d.violatingSlides === 0
                                                            ? "bg-gray-200"
                                                            : d.slideViolationRate >
                                                                ALERT_RATE
                                                              ? "bg-[#d50000]"
                                                              : "bg-secondary"
                                                    )}
                                                    style={{
                                                        height: `${Math.max(
                                                            (d.slideViolationRate /
                                                                maxDay) *
                                                                100,
                                                            4
                                                        )}%`,
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Label the bars. An unlabelled bar chart tells you a
                                        shape, not a fact. */}
                                    <div className="flex gap-2">
                                        {metrics.trend.map((d) => (
                                            <div
                                                key={d.day}
                                                className="w-10 text-center text-[10px] leading-tight text-gray-500"
                                            >
                                                <div className="font-semibold text-primary">
                                                    {pct(d.slideViolationRate)}
                                                </div>
                                                <div>{d.day.slice(5)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-[10px] text-gray-400">
                                    dashed line = 2% alert threshold
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CaptionViolationMetricCards;
